import { evidenceDisposalTypesApi, evidenceKindsApi, evidenceTypesApi } from './evidenceApi';
import type { EvidenceTermListItem } from './evidenceTypes';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';

/** A kind or a type, as the shared Settings pages read it. Only a kind carries the file flag. */
export type VocabularyItem = EvidenceTermListItem & { storesFile?: boolean };

export type VocabularyKey = 'kinds' | 'types' | 'disposal-types';

interface VocabularyRequest {
  name: string;
  description: string | null;
  storesFile: boolean;
}

/** A term vocabulary carries no file flag; the shared request does, and it is not sent. */
function terms(api: typeof evidenceTypesApi) {
  return {
    ...api,
    create: ({ name, description }: VocabularyRequest) => api.create({ name, description }),
    update: (id: string, { name, description }: VocabularyRequest) => api.update(id, { name, description }),
  };
}

/**
 * The vocabularies the intake and the markings grow, described once so their Settings pages, profiles and
 * modal are ONE implementation — a kind is a type with one more field, not a second screen.
 */
export const VOCABULARIES: Record<
  VocabularyKey,
  {
    module: 'evidence-kinds' | 'evidence-types' | 'evidence-disposal-types';
    singular: 'kind' | 'type' | 'disposal type';
    /** The id slug — `evidence-${slug}`. */
    slug: string;
    title: string;
    route: string;
    entityType: string;
    hasFileFlag: boolean;
    nameDescription: string;
    emptyMessage: string;
    api: {
      list: (query: ListQuery, signal?: AbortSignal) => Promise<PagedResult<VocabularyItem>>;
      get: (id: string, signal?: AbortSignal) => Promise<VocabularyItem>;
      create: (request: VocabularyRequest) => Promise<VocabularyItem>;
      update: (id: string, request: VocabularyRequest) => Promise<VocabularyItem>;
      deactivate: (id: string) => Promise<void>;
      activate: (id: string) => Promise<void>;
    };
  }
> = {
  kinds: {
    module: 'evidence-kinds',
    singular: 'kind',
    slug: 'kind',
    title: 'Kinds',
    route: '/evidence/settings/kinds',
    entityType: 'EvidenceKind',
    hasFileFlag: true,
    nameDescription: 'Physical, Digital, or another kind of evidence. Unique among live kinds.',
    emptyMessage: 'No kinds yet. Physical and Digital are added when the system starts.',
    api: evidenceKindsApi,
  },
  types: {
    module: 'evidence-types',
    singular: 'type',
    slug: 'type',
    title: 'Types',
    route: '/evidence/settings/types',
    entityType: 'EvidenceType',
    hasFileFlag: false,
    nameDescription: 'What an item is — a knife, a substance, a phone. Unique among live types.',
    emptyMessage: 'No types yet. Add them here, or type a new one into the intake form.',
    api: terms(evidenceTypesApi),
  },
  'disposal-types': {
    module: 'evidence-disposal-types',
    singular: 'disposal type',
    slug: 'disposal-type',
    title: 'Disposal Types',
    route: '/evidence/settings/disposal-types',
    entityType: 'EvidenceDisposalType',
    hasFileFlag: false,
    nameDescription: 'How evidence is disposed of — burned, returned to its owner, forfeited. Unique among live disposal types.',
    emptyMessage: "No disposal types yet. Add them here, or type a new one into the Transfer form's Disposal type.",
    api: terms(evidenceDisposalTypesApi),
  },
};
